/**
 * GA4 event helpers. Keeps the `gtag` fallback shim in one place so
 * callers can `import { trackSubscription } from "../lib/analytics"`
 * without worrying about whether the CDN script has loaded yet.
 *
 * The base gtag() bootstrap lives in `public/index.html` and pushes to
 * `window.dataLayer` before the async script arrives — GA4 drains the
 * queue on load, so early events are never lost.
 */

const GA4_MEASUREMENT_ID = "G-17RXRHNL5S";

// One-and-only wrapper around gtag. Falls back to a dataLayer push when
// the CDN hasn't finished loading (rare but possible on a fast redirect
// from Stripe checkout back into /settings).
function callGtag() {
  const args = arguments;
  const gtag = window.gtag
    || function () { (window.dataLayer = window.dataLayer || []).push(arguments); };
  gtag.apply(null, args);
}

/**
 * Fire the "purchase" conversion when a Stripe checkout completes.
 * GA4 recognises `purchase` as a standard e-commerce conversion out of
 * the box — no need to configure a custom event in the dashboard.
 *
 * Deduped per session_id via sessionStorage so a page refresh on
 * `/settings?checkout=success&session_id=…` doesn't double-count.
 *
 * @param {object} p
 * @param {string} p.sessionId  Stripe Checkout Session id (unique transaction key)
 * @param {string} [p.tier]     "growth" | "team" | "broker"
 * @param {string} [p.tierName] "Growth" | "Master" | "Pro"
 * @param {number} [p.value]    USD amount, e.g. 39.99
 */
export function trackSubscription({ sessionId, tier, tierName, value }) {
  if (!sessionId) return;
  const dedupeKey = `ga4:purchase:${sessionId}`;
  try {
    if (window.sessionStorage?.getItem(dedupeKey)) return;
    window.sessionStorage?.setItem(dedupeKey, "1");
  } catch (_) { /* private mode / storage disabled — still fire */ }

  const params = {
    send_to: GA4_MEASUREMENT_ID,
    transaction_id: sessionId,
    currency: "USD",
    value: Number.isFinite(value) ? Number(value) : undefined,
    items: [{
      item_id: tier || "subscription",
      item_name: tierName || "iKEY Subscription",
      item_category: "subscription",
      price: Number.isFinite(value) ? Number(value) : undefined,
      quantity: 1,
    }],
  };

  // 1) Standard GA4 e-commerce conversion event.
  callGtag("event", "purchase", params);

  // 2) Named "new_subscription" so folks searching the GA4 event list
  //    for "subscription" find it. Mark it up for the same conversion
  //    dashboard by pointing at the same measurement ID.
  callGtag("event", "new_subscription", {
    send_to: GA4_MEASUREMENT_ID,
    transaction_id: sessionId,
    tier: tier || undefined,
    tier_name: tierName || undefined,
    value: params.value,
    currency: "USD",
  });
}
